import { useTranslation } from 'react-i18next'
import type { PresentationMeta, PresentationStatus } from '../store/registrationsSlice'
import { usePresentationFileAvailable, downloadPresentationFile } from '../utils/presentationFiles'
import { formatLocalDateTime } from '../utils/datetime'

// ── Brand-token styles per status ──────────────────────────────────────────────

const PRESENTATION_STYLES: Record<PresentationStatus, string> = {
  none: 'text-pax-gray',
  reminder_sent: 'bg-pax-yellow/15 text-pax-yellow',
  uploaded: 'bg-pax-blue/10 text-pax-blue',
  accepted: 'bg-pax-green/15 text-pax-green',
  rejected: 'bg-pax-red/10 text-pax-red',
}

// ── Download icon ──────────────────────────────────────────────────────────────

const DownloadIcon = () => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
)

// ── Helper: build the display label ───────────────────────────────────────────

function buildLabel(
  status: PresentationStatus,
  meta: PresentationMeta | undefined,
  t: (key: string) => string,
  locale: string
): string {
  const baseLabel = t(`pres_status_${status}`)

  if (status === 'reminder_sent' && meta?.lastReminderAt) {
    return `${baseLabel} · ${formatLocalDateTime(meta.lastReminderAt, locale)}`
  }
  if (
    (status === 'uploaded' || status === 'accepted' || status === 'rejected') &&
    meta?.uploadedAt
  ) {
    return `${baseLabel} · ${formatLocalDateTime(meta.uploadedAt, locale)}`
  }
  return baseLabel
}

// ── PresentationBadge ─────────────────────────────────────────────────────────

interface PresentationBadgeProps {
  /** The registration id — used to look up the blob in IndexedDB. */
  registrationId: string
  meta: PresentationMeta | undefined
}

/**
 * Renders the presentation status as a colored pill.
 * When a downloadable blob is available in IndexedDB (uploaded / accepted),
 * the pill becomes a clickable download button.
 * Availability is checked asynchronously so the pill is reactive across tabs.
 */
export function PresentationBadge({ registrationId, meta }: PresentationBadgeProps) {
  const { t, i18n } = useTranslation()
  const status: PresentationStatus = meta?.status ?? 'none'

  const canDownload = usePresentationFileAvailable(registrationId, status)

  if (status === 'none') {
    return <span className="text-pax-gray text-sm">—</span>
  }

  const label = buildLabel(status, meta, t, i18n.language)
  const pillClass = `inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-semibold tracking-wide uppercase ${PRESENTATION_STYLES[status]}`

  if (canDownload) {
    return (
      <button
        type="button"
        onClick={() => downloadPresentationFile(registrationId)}
        title={t('pres_download')}
        className={`${pillClass} cursor-pointer hover:underline focus:outline-none focus-visible:ring-2`}
      >
        <DownloadIcon />
        <span>{label}</span>
      </button>
    )
  }

  return (
    <span className={pillClass}>
      <span>{label}</span>
    </span>
  )
}
